import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Stack,
  InputAdornment
} from '@mui/material';
import { ColorLens as ColorIcon } from '@mui/icons-material';
import { severityApi } from '../../services/api';

const SeverityLevelDialog = ({ 
  open, 
  onClose, 
  mode = 'create', // 'create' or 'edit'
  schemeId,
  level = null,
  onUpdate 
}) => {
  const [formData, setFormData] = useState({
    scheme_id: schemeId || '',
    name: '',
    value: '',
    description: '',
    color_code: '#FF0000'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && level) {
        setFormData({
          scheme_id: level.scheme_id || schemeId || '',
          name: level.name || '',
          value: level.value?.toString() || '',
          description: level.description || '',
          color_code: level.color_code || '#FF0000'
        });
      } else {
        setFormData({
          scheme_id: schemeId || '',
          name: '',
          value: '',
          description: '',
          color_code: '#FF0000'
        });
      }
      setError(null);
    }
  }, [open, mode, level, schemeId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Severity level name is required');
      return;
    }

    if (!formData.value || isNaN(parseInt(formData.value))) {
      setError('Severity level value must be a valid number');
      return;
    }

    if (!formData.scheme_id) {
      setError('Scheme ID is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submissionData = {
        ...formData,
        value: parseInt(formData.value)
      };

      if (mode === 'edit' && level) {
        await severityApi.updateLevel(level.id, submissionData);
      } else {
        await severityApi.createLevel(submissionData);
      }

      // Notify parent component to refresh data
      if (onUpdate) {
        await onUpdate();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error saving severity level:', err);
      setError(err.response?.data?.error || 'Failed to save severity level');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!level || mode !== 'edit') return;

    if (!window.confirm(`Are you sure you want to delete the severity level "${level.name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await severityApi.deleteLevel(level.id);

      // Notify parent component to refresh data
      if (onUpdate) {
        await onUpdate();
      }

      // Close dialog
      onClose();
    } catch (err) {
      console.error('Error deleting severity level:', err);
      setError(err.response?.data?.error || 'Failed to delete severity level');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {mode === 'edit' ? 'Edit Severity Level' : 'Create New Severity Level'}
        </Typography>
        {schemeId && (
          <Typography variant="body2" color="text.secondary">
            Scheme ID: {schemeId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          <TextField
            label="Level Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            fullWidth
            helperText="Enter a descriptive name for this severity level (e.g., 'Low', 'Medium', 'High')"
          />

          <TextField
            label="Level Value"
            type="number"
            value={formData.value}
            onChange={(e) => handleInputChange('value', e.target.value)}
            required
            fullWidth
            helperText="Enter a numeric value for ordering (lower values = less severe)"
            inputProps={{ min: 0, step: 1 }}
          />

          <TextField
            label="Color Code"
            value={formData.color_code}
            onChange={(e) => handleInputChange('color_code', e.target.value)}
            required
            fullWidth
            helperText="Hex color code for visual representation"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: formData.color_code,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ColorIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            helperText="Optional detailed description of when this severity level applies"
          />

          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Preview:</strong>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: formData.color_code,
                  color: 'white',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 'medium'
                }}
              >
                {formData.name || 'Level Name'} ({formData.value || 'Value'})
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {mode === 'edit' && (
          <Button
            onClick={handleDelete}
            color="error"
            disabled={loading}
            sx={{ mr: 'auto' }}
          >
            Delete Level
          </Button>
        )}
        
        <Button 
          onClick={onClose} 
          disabled={loading}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {mode === 'edit' ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeverityLevelDialog; 